/**
 * FeedDeliveryDetailPage -- one core deposit feed delivery (GH #2911, LOI
 * section 2.2): its manifest, how it reconciled against its own control
 * totals, and the records it landed with their provenance stamps.
 *
 * Route: /core-deposit-feed/deliveries/:deliveryId. Outside /tempo for the
 * reason CoreDepositFeedPage states; the server's FEED_READ_ROLES check is the
 * gate.
 *
 * Reads (core-deposit-feed.openapi.yaml):
 *   GET /api/v1/core-deposit-feed/deliveries/{deliveryId}           getFeedDelivery
 *   GET /api/v1/core-deposit-feed/deliveries/{deliveryId}/records   listDeliveryRecords
 *
 * The reconciliation section reads `FeedDelivery.reconciliation`, the same
 * ReconciliationResult getDeliveryReconciliation returns; a delivery that is
 * still OPEN has none, and the section says so.
 *
 * AN ABSENCE STATE RENDERS ONLY WHEN THE READ SUCCEEDED.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  BodyText,
  Button,
  Card,
  EmptyState,
  Heading,
  Select,
  Skeleton,
  Table,
  type TableColumn,
} from '@aaimplatform/ui';
import {
  useDeliveryRecords,
  useFeedDelivery,
  type CoreFeedRecordType,
  type FeedDelivery,
  type LandedRecord,
  type ReconciliationDiscrepancy,
  type RecordTypeReconciliation,
} from '../../hooks/useCoreDepositFeed';
import {
  ABSENT,
  AmountTotalList,
  DeliveryStatusBadge,
  FieldRow,
  SURFACE_DARK_DESCRIPTION,
  formatCount,
  formatDate,
  formatDateTime,
} from './FeedParts';
import { deliveryDetailPath } from './CoreDepositFeedPage';

/** Schema `CoreFeedRecordType`, in contract order. */
const RECORD_TYPES: ReadonlyArray<CoreFeedRecordType> = [
  'DEPOSIT_ACCOUNT',
  'ACCOUNT_HOLDER_LINK',
  'TRANSACTION',
  'BALANCE_SNAPSHOT',
  'HOLD',
  'INSUFFICIENT_FUNDS_EVENT',
  'PENDING_DEBIT',
  'SCHEDULED_PAYMENT',
];

/** A sha256 hex digest shortened for a table cell; the full value is the title. */
function shortHash(value: string): string {
  return value.length > 16 ? `${value.slice(0, 16)}...` : value;
}

function DeliveryLink({ deliveryId }: { deliveryId: string | null | undefined }) {
  if (!deliveryId) return <span className="text-tertiary">{ABSENT}</span>;
  return (
    <Link to={deliveryDetailPath(deliveryId)} className="text-accent underline">
      {deliveryId}
    </Link>
  );
}

function ManifestCard({ delivery }: { delivery: FeedDelivery }) {
  const manifest = delivery.manifest;
  return (
    <Card className="p-4">
      <Heading level={3} size="card">Manifest</Heading>
      <div className="mt-3">
        <FieldRow label="status" value={<DeliveryStatusBadge status={delivery.status} />} />
        <FieldRow label="receivedAt" value={formatDateTime(delivery.receivedAt)} />
        <FieldRow label="sealedAt" value={formatDateTime(delivery.sealedAt)} />
        <FieldRow label="replay" value={delivery.replay ? 'Yes' : 'No'} />
        <FieldRow label="restatementIntent" value={manifest.restatementIntent} />
        <FieldRow label="supersedesDeliveryId" value={<DeliveryLink deliveryId={manifest.supersedesDeliveryId} />} />
        <FieldRow label="supersededByDeliveryId" value={<DeliveryLink deliveryId={delivery.supersededByDeliveryId} />} />
        <FieldRow label="fileId" value={manifest.fileId} />
        <FieldRow label="coreSystemId" value={manifest.coreSystemId} />
        <FieldRow label="adapterId" value={manifest.adapterId} />
        <FieldRow label="adapterVersion" value={manifest.adapterVersion} />
        <FieldRow label="businessDate" value={formatDate(manifest.businessDate)} />
        <FieldRow label="businessTimeZone" value={manifest.businessTimeZone} />
        <FieldRow label="coverageFrom" value={formatDate(manifest.coverageFrom)} />
        <FieldRow label="coverageTo" value={formatDate(manifest.coverageTo)} />
        <FieldRow label="declaredRecordCount" value={formatCount(manifest.declaredRecordCount)} />
        <FieldRow label="payloadSha256" value={manifest.payloadSha256} />
        <FieldRow label="sourceGeneratedAt" value={formatDateTime(manifest.sourceGeneratedAt)} />
      </div>
    </Card>
  );
}

function ReconciliationCard({ delivery }: { delivery: FeedDelivery }) {
  const reconciliation = delivery.reconciliation ?? null;

  const perTypeColumns: TableColumn<RecordTypeReconciliation>[] = [
    {
      key: 'recordType',
      label: 'Record type',
      render: (_value, row) => <span className="font-mono">{row.recordType}</span>,
    },
    {
      key: 'declaredRecordCount',
      label: 'Declared',
      align: 'right',
      render: (_value, row) => formatCount(row.declaredRecordCount),
    },
    {
      key: 'observedRecordCount',
      label: 'Observed',
      align: 'right',
      render: (_value, row) => formatCount(row.observedRecordCount),
    },
    {
      key: 'declaredAmountTotals',
      label: 'Declared totals',
      render: (_value, row) => <AmountTotalList totals={row.declaredAmountTotals} />,
    },
    {
      key: 'observedAmountTotals',
      label: 'Observed totals',
      render: (_value, row) => <AmountTotalList totals={row.observedAmountTotals} />,
    },
  ];

  const discrepancyColumns: TableColumn<ReconciliationDiscrepancy>[] = [
    {
      key: 'kind',
      label: 'Kind',
      render: (_value, row) => <span className="font-mono">{row.kind}</span>,
    },
    {
      key: 'recordType',
      label: 'Record type',
      render: (_value, row) => <span className="font-mono">{row.recordType ?? ABSENT}</span>,
    },
    {
      // The four per-record kinds carry a fixed detail sentence and null
      // values, so the hash is the only thing naming the record a row rejects.
      key: 'recordHash',
      label: 'Record hash',
      render: (_value, row) =>
        row.recordHash ? (
          <span className="font-mono" title={row.recordHash}>
            {shortHash(row.recordHash)}
          </span>
        ) : (
          <span className="font-mono">{ABSENT}</span>
        ),
    },
    {
      key: 'measure',
      label: 'Measure',
      render: (_value, row) => <span className="font-mono">{row.measure ?? ABSENT}</span>,
    },
    {
      key: 'declaredValue',
      label: 'Declared',
      align: 'right',
      render: (_value, row) => <span className="font-mono">{row.declaredValue ?? ABSENT}</span>,
    },
    {
      key: 'observedValue',
      label: 'Observed',
      align: 'right',
      render: (_value, row) => <span className="font-mono">{row.observedValue ?? ABSENT}</span>,
    },
    {
      key: 'detail',
      label: 'Detail',
      render: (_value, row) => row.detail,
    },
  ];

  return (
    <Card className="p-4">
      <Heading level={3} size="card">Reconciliation</Heading>
      <BodyText variant="ui" className="text-xs text-tertiary mt-1">
        The delivery&apos;s own declared control totals against what tempo-engine observed when
        the delivery was sealed.
      </BodyText>
      <div className="mt-3">
        {!reconciliation && (
          <EmptyState
            title="Not reconciled"
            description={
              delivery.status === 'OPEN'
                ? 'This delivery is OPEN. Reconciliation runs when the adapter seals it.'
                : 'tempo-engine returned no reconciliation for this delivery.'
            }
          />
        )}
        {reconciliation && (
          <div className="space-y-4">
            <div>
              <FieldRow label="outcome" value={reconciliation.outcome} />
              <FieldRow label="declaredRecordCount" value={formatCount(reconciliation.declaredRecordCount)} />
              <FieldRow label="observedRecordCount" value={formatCount(reconciliation.observedRecordCount)} />
              <FieldRow label="payloadHashMatched" value={reconciliation.payloadHashMatched ? 'Yes' : 'No'} />
              <FieldRow label="reconciledAt" value={formatDateTime(reconciliation.reconciledAt)} />
            </div>
            {reconciliation.perRecordType.length > 0 && (
              <Table columns={perTypeColumns} data={reconciliation.perRecordType} />
            )}
            <div>
              <Heading level={4} size="card">Discrepancies</Heading>
              <div className="mt-2">
                {reconciliation.discrepancies.length === 0 ? (
                  <BodyText variant="ui" className="text-sm text-secondary">
                    None recorded.
                  </BodyText>
                ) : (
                  <Table columns={discrepancyColumns} data={reconciliation.discrepancies} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function RecordsCard({ deliveryId }: { deliveryId: string }) {
  const [recordType, setRecordType] = useState<'' | CoreFeedRecordType>('');
  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useDeliveryRecords(deliveryId, recordType || undefined);

  const records = data?.pages.flatMap((page) => page.items) ?? [];
  const surfaceDark = data?.pages[0]?.surfaceDark ?? false;

  const columns: TableColumn<LandedRecord>[] = [
    {
      key: 'recordType',
      label: 'Record type',
      render: (_value, row) => <span className="font-mono">{row.record.recordType}</span>,
    },
    {
      key: 'sourceAccountRef',
      label: 'Source account',
      render: (_value, row) => <span className="font-mono">{row.record.sourceAccountRef}</span>,
    },
    {
      key: 'businessDate',
      label: 'Business date',
      render: (_value, row) => <span className="font-mono">{formatDate(row.record.businessDate)}</span>,
    },
    {
      key: 'recordHash',
      label: 'Record hash',
      render: (_value, row) => (
        <span className="font-mono" title={row.record.recordHash}>
          {shortHash(row.record.recordHash)}
        </span>
      ),
    },
    {
      key: 'provenanceChannel',
      label: 'Channel',
      render: (_value, row) => <span className="font-mono">{row.provenance.provenanceChannel}</span>,
    },
    {
      key: 'visibleToConsumers',
      // LandedRecord.visibleToConsumers is about the downstream feature
      // computations that read landed records, never about what a member sees.
      label: 'Counts toward features',
      render: (_value, row) =>
        row.visibleToConsumers === undefined ? ABSENT : row.visibleToConsumers ? 'Yes' : 'No',
    },
    {
      key: 'ingestedAt',
      label: 'Ingested',
      render: (_value, row) => formatDateTime(row.provenance.ingestedAt),
    },
  ];

  return (
    <Card className="p-4">
      <Heading level={3} size="card">Landed records</Heading>
      <BodyText variant="ui" className="text-xs text-tertiary mt-1">
        Each record with the provenance stamp tempo-engine wrote when it landed.
      </BodyText>
      <div className="flex flex-wrap items-end gap-4 mt-3">
        <Select
          label="Record type"
          value={recordType}
          onChange={(event) => setRecordType(event.target.value as '' | CoreFeedRecordType)}
          options={[
            { value: '', label: 'All record types' },
            ...RECORD_TYPES.map((type) => ({ value: type, label: type })),
          ]}
          fullWidth={false}
        />
      </div>
      <div className="mt-4">
        {isLoading && <Skeleton className="h-48 w-full" />}
        {error && (
          <Alert type="error" title="Records load failed">
            {error.message}
          </Alert>
        )}
        {!isLoading && !error && surfaceDark && (
          <EmptyState title="Records not available" description={SURFACE_DARK_DESCRIPTION} />
        )}
        {!isLoading && !error && !surfaceDark && records.length === 0 && (
          <EmptyState
            title="No landed records"
            description="tempo-engine holds no landed record of this delivery that matches the filter."
          />
        )}
        {records.length > 0 && (
          <>
            <Table columns={columns} data={records} />
            {hasNextPage && (
              <div className="mt-3">
                <Button
                  variant="secondary"
                  disabled={isFetchingNextPage}
                  onClick={() => {
                    void fetchNextPage();
                  }}
                >
                  {isFetchingNextPage ? 'Loading' : 'Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

export function FeedDeliveryDetailPage() {
  const { deliveryId } = useParams<{ deliveryId: string }>();
  const { data: delivery, isLoading, error } = useFeedDelivery(deliveryId);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/core-deposit-feed" className="text-sm text-accent underline">
          Core Deposit Feed
        </Link>
        {/* Level 2: PortalLayout's AppHeader renders the page h1. */}
        <div className="mt-2">
          <Heading level={2} size="page">Delivery</Heading>
        </div>
        <BodyText variant="ui" className="text-sm text-secondary mt-1 font-mono break-all">
          {deliveryId ?? ABSENT}
        </BodyText>
      </div>
      {isLoading && <Skeleton className="h-64 w-full" />}
      {error && (
        <Alert type="error" title="Delivery load failed">
          {error.message}
        </Alert>
      )}
      {!isLoading && !error && delivery === null && (
        <EmptyState
          title="Delivery not found"
          description={`No delivery with this id answered for this session. ${SURFACE_DARK_DESCRIPTION}`}
        />
      )}
      {delivery && deliveryId && (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            <ManifestCard delivery={delivery} />
            <ReconciliationCard delivery={delivery} />
          </div>
          <RecordsCard deliveryId={deliveryId} />
        </>
      )}
    </div>
  );
}
